import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTeachers, useDeleteTeacher } from "@/lib/api/teachers/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Edit, Search } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { AdminGuard } from "@/components/shared/admin-guard";
import { toast } from "sonner";
import { TeacherEditDialog } from "@/components/teachers/teacher-edit-dialog";
import { Teacher } from "@/lib/api/teachers/types";
import { ApiErrorResponse } from "@growing/types";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

function TeachersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const { data: teachersData, isLoading } = useTeachers({
    search,
    page,
    limit: 20,
  });
  const deleteTeacher = useDeleteTeacher();

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDelete = (id: string, name: string) => {
    setAlertDialogState({
      title: "선생님 삭제",
      description: `정말 "${name}" 선생님을 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteTeacher.mutateAsync(id);
          toast.success("삭제되었습니다.");
        } catch (error: unknown) {
          const apiError = error as ApiErrorResponse;
          const message =
            apiError.response?.data?.message || "삭제에 실패했습니다.";
          toast.error(message);
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  useLoadingIndicator(isLoading);

  return (
    <AdminGuard>
      <PageContainer>
        <Header title="선생님 관리" />

        {/* 검색 */}
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="이름 또는 아이디로 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            wrapperClassName="max-w-sm"
          />
          <Button onClick={handleSearch} variant="outline">
            <Search /> 검색
          </Button>
        </div>

        {/* 선생님 목록 */}
        <div className="bg-card rounded-lg border">
          {teachersData && teachersData.teachers.length > 0 ? (
            <>
              {/* 테이블 헤더 */}
              <div className="hidden md:grid grid-cols-7 gap-4 p-4 border-b font-semibold text-sm text-muted-foreground">
                <div>이름</div>
                <div>아이디</div>
                <div>전화번호</div>
                <div>생년월일</div>
                <div>역할</div>
                <div>소속 지점</div>
                <div></div>
              </div>

              {/* 테이블 바디 */}
              <div className="divide-y">
                {teachersData.teachers.map((teacher) => (
                  <div
                    key={teacher._id}
                    className="p-4 hover:bg-muted/50 transition-colors"
                  >
                    {/* 데스크톱 뷰 */}
                    <div className="hidden md:grid grid-cols-7 gap-4 items-center">
                      <div className="font-medium">{teacher.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {teacher.userId}
                      </div>
                      <div className="text-sm">
                        {formatPhoneNumber(teacher.phoneNumber)}
                      </div>
                      <div className="text-sm">
                        {formatBirthDate(teacher.birthDate)}
                      </div>
                      <div>
                        <span
                          className={`inline-flex px-2 py-1 text-xs rounded ${
                            teacher.role === "admin"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {teacher.role === "admin" ? "관리자" : "선생님"}
                        </span>
                      </div>
                      <div className="text-sm">
                        {teacher.office?.name || "-"}
                      </div>
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingTeacher(teacher)}
                        >
                          <Edit className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleDelete(teacher._id, teacher.name)
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>

                    {/* 모바일 뷰 */}
                    <div className="md:hidden space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{teacher.name}</span>
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded ${
                              teacher.role === "admin"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {teacher.role === "admin" ? "관리자" : "선생님"}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingTeacher(teacher)}
                          >
                            <Edit className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleDelete(teacher._id, teacher.name)
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {teacher.userId} ·{" "}
                        {formatPhoneNumber(teacher.phoneNumber)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatBirthDate(teacher.birthDate)}
                        {teacher.office?.name &&
                          ` · ${teacher.office.name}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              {teachersData.totalPages > 1 && (
                <div className="flex gap-2 p-4 justify-center border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    이전
                  </Button>
                  <span className="flex items-center px-4 text-sm">
                    {page} / {teachersData.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === teachersData.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    다음
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              {search ? "검색 결과가 없습니다." : "등록된 선생님이 없습니다."}
            </div>
          )}
        </div>

        {/* 수정 다이얼로그 */}
        {editingTeacher && (
          <TeacherEditDialog
            teacher={editingTeacher}
            open={!!editingTeacher}
            onOpenChange={(open) => !open && setEditingTeacher(null)}
          />
        )}
      </PageContainer>
    </AdminGuard>
  );
}

function formatPhoneNumber(phone: string): string {
  if (phone.length === 11) {
    return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
  }
  return phone;
}

function formatBirthDate(birthDate: string): string {
  if (birthDate.length === 6) {
    return `${birthDate.slice(0, 2)}.${birthDate.slice(2, 4)}.${birthDate.slice(
      4
    )}`;
  }
  return birthDate;
}

export const Route = createFileRoute("/_content/teachers")({
  component: TeachersPage,
});
